import { CommonModule } from '@angular/common';
import { MaskService } from '../../../../../shared/services/mask.service';
import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxCurrencyDirective } from 'ngx-currency';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { ContasReceberService } from '../../../../../shared/services/apis/financas/contas-receber.service';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { TipoParcelamento } from '../../../../../shared/services/models/financas/tipo-parcelamento.model';
import { ContaFinanceira } from '../../../../../shared/services/models/financas/conta-financeira.model';
import { FormaRecebimento } from '../../../../../shared/services/models/financas/forma-recebimento.model';
import { PessoaService } from '../../../../../shared/services/apis/pessoa/pessoa.service';
import { MunicipioService } from '../../../../../shared/services/apis/usuario/municipio.service';
import { SacadoBoletoStruct } from '../../../../../shared/services/structs/financas/sacado-boleto.struct';
import { CodigoSefazEnum } from '../../../../../shared/enums/financas/codigo-sefaz.enum';
import { UtilService } from '../../../../../shared/services/util.service';

export interface RenegociacaoModalData {
    idContasReceber: number;
    idCliente: number;
    valorEmAberto: number;
    valorTotal: number;
    descricaoDocumento: string;
    listaTiposParcelamento: TipoParcelamento[];
    listaContaFinanceira: ContaFinanceira[];
    listaFormaRecebimento: FormaRecebimento[];
    valorBilhetagemBoleto: number;
    currencyMask: any;
}

@Component({
    selector: 'app-modal-renegociacao-parcelas',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatIconModule,
        MatProgressSpinnerModule,
        NgxCurrencyDirective,
        SelectPesquisavelComponent
    ],
    templateUrl: './modal-renegociacao-parcelas.component.html',
    styleUrl: './modal-renegociacao-parcelas.component.css'
})
export class ModalRenegociacaoParcelasComponent implements OnInit {

    public form: FormGroup;
    public isSimulando: boolean = false;
    public isSalvando: boolean = false;
    public simulado: boolean = false;
    public integerMask: any;

    get parcelasArray(): FormArray {
        return this.form.get('parcelas') as FormArray;
    }

    get idFormaRecebimentoControl(): any { return this.form.get('idFormaRecebimento'); }
    get idContaFinanceiraControl(): any { return this.form.get('idContaFinanceira'); }

    get tipoParcelamentoSelecionado(): TipoParcelamento | null {
        const id = this.form.get('idTipoParcelamento')?.value;
        if (!id) return null;
        return this.data.listaTiposParcelamento.find(t => t.idTipoParcelamento === id) ?? null;
    }

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: RenegociacaoModalData,
        public dialogRef: MatDialogRef<ModalRenegociacaoParcelasComponent>,
        private fb: FormBuilder,
        private contasReceberService: ContasReceberService,
        private alertService: AlertService,
        private dialog: MatDialog,
        private pessoaService: PessoaService,
        private municipioService: MunicipioService,
        private maskService: MaskService
    ) {
        this.integerMask = this.maskService.getIntegerMask();
        this.form = this.fb.group({
            idTipoParcelamento: [null],
            dataVencimento: [''],
            intervaloDiasParcelas: [30],
            idFormaRecebimento: [null],
            idContaFinanceira: [null],
            parcelas: this.fb.array([])
        });
    }

    ngOnInit(): void {
        // Reset simulação ao trocar tipo parcelamento
        this.form.get('idTipoParcelamento')?.valueChanges.subscribe(() => {
            this.parcelasArray.clear();
            this.simulado = false;
        });
    }

    public simular(): void {
        const idTipoParcelamento = this.form.get('idTipoParcelamento')?.value;
        const dataVencimento = this.form.get('dataVencimento')?.value;
        const intervaloDiasParcelas = this.form.get('intervaloDiasParcelas')?.value;

        if (!idTipoParcelamento) {
            this.alertService.show('Atenção', 'Selecione o tipo de parcelamento.', AlertType.warning);
            return;
        }
        if (!dataVencimento) {
            this.alertService.show('Atenção', 'Informe a data de vencimento inicial.', AlertType.warning);
            return;
        }

        const request = {
            valorTotal: this.data.valorEmAberto,
            idTipoParcelamento,
            dataVencimento: new Date(dataVencimento).toISOString(),
            intervaloDiasParcelas: intervaloDiasParcelas || 30,
            descricaoBase: this.data.descricaoDocumento
        };

        this.isSimulando = true;
        this.contasReceberService.simularParcelas(request).subscribe({
            next: (res: any) => {
                this.isSimulando = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao simular parcelas.', AlertType.error);
                    return;
                }
                this.popularGrid(res.parcelas || []);
                this.simulado = true;
            },
            error: () => {
                this.isSimulando = false;
                this.alertService.show('Erro', 'Erro ao comunicar-se com o servidor.', AlertType.error);
            }
        });
    }

    private popularGrid(parcelas: any[]): void {
        this.parcelasArray.clear();
        parcelas.forEach((p: any) => {
            this.parcelasArray.push(this.fb.group({
                nroParcela: [p.nroParcela],
                dataVencimento: [p.dataVencimento ? new Date(p.dataVencimento) : null],
                valor: [p.valor],
                percentual: [p.percentual],
                descricao: [p.descricao]
            }));
        });
    }

    public onParcelaValorChange(index: number): void {
        const ctrl = this.parcelasArray.at(index);
        const valor = ctrl.get('valor')?.value || 0;
        const perc = this.data.valorEmAberto > 0 ? Math.round((valor / this.data.valorEmAberto) * 10000) / 100 : 0;
        ctrl.get('percentual')?.setValue(perc, { emitEvent: false });
    }

    public confirmar(): void {
        if (!this.simulado || this.parcelasArray.length === 0) {
            this.alertService.show('Atenção', 'É necessário simular as parcelas antes de confirmar.', AlertType.warning);
            return;
        }

        const parcelas = this.parcelasArray.controls.map((ctrl: any) => ({
            nroParcela: ctrl.get('nroParcela')?.value,
            dataVencimento: ctrl.get('dataVencimento')?.value ? new Date(ctrl.get('dataVencimento').value).toISOString() : null,
            valor: ctrl.get('valor')?.value || 0,
            descricao: ctrl.get('descricao')?.value,
            idFormaRecebimento: this.form.get('idFormaRecebimento')?.value,
            idContaFinanceira: this.form.get('idContaFinanceira')?.value
        }));

        const request = {
            idContasReceber: this.data.idContasReceber,
            idTipoParcelamento: this.form.get('idTipoParcelamento')?.value,
            dataVencimento: new Date(this.form.get('dataVencimento')?.value).toISOString(),
            intervaloDiasParcelas: this.form.get('intervaloDiasParcelas')?.value,
            idFormaRecebimento: this.form.get('idFormaRecebimento')?.value,
            idContaFinanceira: this.form.get('idContaFinanceira')?.value,
            parcelas
        };

        // Confirm prévio: a renegociação CANCELA as parcelas em aberto e cria novas. As cobranças (PIX/boleto)
        // ativas dessas parcelas serão canceladas/baixadas — boletos sem baixa automática serão baixados manualmente.
        this.dialog.open(ConfirmModalComponent, {
            width: '460px',
            data: {
                title: 'Atenção — Renegociação',
                innerHtml: 'As parcelas em aberto deste documento serão <strong>canceladas</strong> e substituídas pelas novas.<br><br>Cobranças (PIX/boleto) ativas dessas parcelas serão canceladas/baixadas. Boletos de bancos que não permitem baixa automática precisarão ser baixados manualmente no portal do banco.<br><br>Deseja continuar?',
                buttonConfirm: 'Continuar',
                buttonClose: 'Voltar',
            }
        }).afterClosed().subscribe((r: any) => { if (r?.confirm) this.decidirGerarBoletosERenegociar(request); });
    }

    /**
     * Se a forma escolhida for boleto e houver conta selecionada, pergunta se deseja gerar os boletos das novas
     * parcelas (com custo de bilhetagem) — espelha o fluxo do cadastro. Anexa gerarBoletos + sacado e renegocia.
     */
    private decidirGerarBoletosERenegociar(request: any): void {
        const idForma = request.idFormaRecebimento;
        const idConta = request.idContaFinanceira;
        const forma = (this.data.listaFormaRecebimento ?? []).find(
            (f: any) => f.id === idForma || f.idFormaRecebimento === idForma
        );
        const isBoleto = !!forma && String((forma as any).codigoPagamentoSefaz) === CodigoSefazEnum.Boleto;

        // O gate real de "conta conectada" é do backend ao gerar; aqui basta forma boleto + conta selecionada.
        // Sem idCliente não há como buscar o sacado — não oferece a geração (renegocia normal; gera depois pela lista).
        if (!isBoleto || !idConta || !this.data.idCliente) {
            this.executarRenegociacao(request);
            return;
        }

        const qtd = this.parcelasArray.length;
        const custoUnit = UtilService.arredondarAbnt(this.data.valorBilhetagemBoleto ?? 0, 2);
        const custoTotal = UtilService.arredondarAbnt(qtd * custoUnit, 2);
        const fmt = (v: number) => 'R$ ' + (v ?? 0).toFixed(2).replace('.', ',');

        this.dialog.open(ConfirmModalComponent, {
            width: '460px',
            data: {
                title: 'Gerar boletos?',
                innerHtml: `A forma de recebimento é <strong>boleto</strong>. Deseja gerar os boletos das <strong>${qtd} nova(s) parcela(s)</strong> agora?`
                    + `<br><br>Custo de bilhetagem: <strong>${fmt(custoUnit)}</strong> por boleto — total <strong>${fmt(custoTotal)}</strong>.`,
                buttonConfirm: 'Sim, gerar boletos',
                buttonClose: 'Não, gerar depois',
            }
        }).afterClosed().subscribe((res: any) => {
            if (res?.confirm) {
                this.pessoaService.getById(this.data.idCliente).subscribe({
                    next: pessoaResp => {
                        if (pessoaResp.isError || !pessoaResp.pessoa) {
                            this.isSalvando = false;
                            this.alertService.show('Erro', pessoaResp.errorDescription || 'Não foi possível obter os dados do cliente.', AlertType.error);
                            return;
                        }

                        const enderecos = pessoaResp.enderecos ?? [];
                        const endereco = enderecos.find(item => item.isCobranca)
                            ?? enderecos.find(item => item.isPrincipal)
                            ?? enderecos[0];
                        const idMunicipio = endereco?.idMunicipio;
                        if (!idMunicipio) {
                            this.isSalvando = false;
                            this.alertService.show('Erro', 'Cliente sem município no endereço de cobrança ou principal.', AlertType.error);
                            return;
                        }

                        this.municipioService.getByIds([idMunicipio]).subscribe({
                            next: municipioResponse => {
                                const municipio = municipioResponse.list?.find(item => item.idMunicipio === idMunicipio);
                                if (municipioResponse.isError || !municipio) {
                                    this.isSalvando = false;
                                    this.alertService.show('Erro', municipioResponse.errorDescription || 'Não foi possível resolver o município do cliente.', AlertType.error);
                                    return;
                                }

                                request.gerarBoletos = true;
                                request.sacado = SacadoBoletoStruct.fromPessoaResponse(pessoaResp, municipio);
                                this.executarRenegociacao(request);
                            },
                            error: () => {
                                this.isSalvando = false;
                                this.alertService.show('Erro', 'Não foi possível consultar o município do cliente.', AlertType.error);
                            }
                        });
                    },
                    error: () => {
                        this.isSalvando = false;
                        this.alertService.show('Erro', 'Não foi possível obter os dados do cliente.', AlertType.error);
                    }
                });
            } else {
                this.executarRenegociacao(request);
            }
        });
    }

    private executarRenegociacao(request: any): void {
        this.isSalvando = true;
        this.contasReceberService.renegociarParcelas(request).subscribe({
            next: (res: any) => {
                this.isSalvando = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao renegociar parcelas.', AlertType.error);
                    return;
                }
                // Resumo da geração de boletos das novas parcelas (quando solicitado)
                const bg = res.boletosGerados;
                if (bg && (bg.sucesso || bg.falha || bg.semIntegracao)) {
                    let msg = `Parcelas renegociadas. Boletos: ${bg.sucesso} gerado(s)`;
                    if (bg.falha) msg += `, ${bg.falha} com falha`;
                    if (bg.semIntegracao) msg += `, ${bg.semIntegracao} sem integração ativa`;
                    msg += '.';
                    this.alertService.show('Sucesso', msg, bg.falha ? AlertType.warning : AlertType.success);
                } else {
                    this.alertService.show('Sucesso', 'Parcelas renegociadas com sucesso!', AlertType.success);
                }
                // avisoDescricao: cancelamento das cobranças (PIX/boleto) das parcelas canceladas — o caller exibe
                this.dialogRef.close({ success: true, avisoDescricao: res.avisoDescricao });
            },
            error: () => {
                this.isSalvando = false;
                this.alertService.show('Erro', 'Erro ao comunicar-se com o servidor.', AlertType.error);
            }
        });
    }

    public cancelar(): void {
        this.dialogRef.close({ success: false });
    }


  onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }
}
